import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, Subject } from 'rxjs';
import { ApiService } from './api.service';
import { ProjectTreeNode } from '@models/project-tree-node.interface';
import { ProjectDto } from '@models/project-dto.interface';
import { EditProjectDto } from '@models/edit-project-dto.interface';
import { ProjectLookupsDto } from '@models/project-lookups-dto.interface';
import { LookupDto } from '@models/lookup-dto.interface';
import { SaveProjectDto } from '@models/save-project-dto.interface';
import { PROJECT_NODE_TYPE } from '@models/project-node-type.const';

@Injectable({
  providedIn: 'root'
})
export class ProjectsService {
  private readonly apiService = inject(ApiService);
  private readonly _resetTree$ = new Subject<boolean>();
  readonly resetTree$ = this._resetTree$.asObservable();
  private readonly _asideExpanded$ = new BehaviorSubject<boolean>(true);
  readonly asideExpanded$ = this._asideExpanded$.asObservable();

  triggerReset(navigate: boolean = true): void {
    this._resetTree$.next(navigate);
  }

  setAsideExpanded(expanded: boolean): void {
    this._asideExpanded$.next(expanded);
  }

  getAvailableYears(): Observable<number[]> {
    return this.apiService.get('projects/years');
  }

  getAssetClasses(): Observable<LookupDto[]> {
    return this.apiService.get('projects/assetclasses');
  }

  getProjectTree(year: number | null, assetClassId: number | null = null): Observable<ProjectTreeNode[]> {
    const params = new URLSearchParams();
    if (year != null) {
      params.set('year', String(year));
    }
    if (assetClassId != null) {
      params.set('assetClassId', String(assetClassId));
    }
    const query = params.toString();
    const path = query ? `projects/tree?${query}` : 'projects/tree';

    return this.apiService.get(path).pipe(
      map((nodes: ProjectTreeNode[]) => this.initTreeState(nodes, true))
    );
  }


  getProject(id: number): Observable<ProjectDto> {
    return this.apiService.get(`projects/${id}`);
  }

  getLookups(): Observable<ProjectLookupsDto> {
    return this.apiService.get('projects/lookups');
  }

  getForEdit(id: number): Observable<EditProjectDto> {
    return this.apiService.get(`projects/${id}/edit`);
  }

  create(dto: SaveProjectDto): Observable<number> {
    return this.apiService.post('projects', dto);
  }

  update(id: number, dto: SaveProjectDto): Observable<void> {
    return this.apiService.put(`projects/${id}`, dto);
  }


  private initTreeState(nodes: ProjectTreeNode[], isRoot: boolean): ProjectTreeNode[] {
    return nodes.map(node => ({
      ...node,
      isExpanded: isRoot && node.data.nodeType === PROJECT_NODE_TYPE.LOOKUP,
      isSelected: false,
      children: node.children?.length
        ? this.initTreeState(node.children, false)
        : []
    }));
  }
}
